// DEV-ONLY · B3 MITARBEITERZUGANG (Preview) — Umsetzung des bereits
// EINGEFRORENEN Visual Standard (B1/B2/B4/Gefahrstoff). Keine neue
// Designentscheidung — Grundlage: ABO_B1_B4_VISUAL_STANDARD_FREEZE_01_UND_B2_B3_FORENSIK.md.
//
//  · Computer = fotorealistischer freigestellter CL-Monitor (device-b3-computer.webp,
//    echter monteur-unterweisung-computer-Screenshot cover-top im imac-frame).
//  · iPhone bleibt (device-handy.webp, fotorealistisch) — überlappend gruppiert,
//    NICHT zum Nebenobjekt geschrumpft. Zwei echte Zugangswege, gleicher Inhalt.
//  · Chris groß & präsent (Pose 17), richtungsrichtig zu den Geräten (nach links),
//    Bubble = Figurengruppe. KEIN kleines Maskottchen mittig unten.
//  · Sehr helle CI, subtiler Sky-/Mint-Lichtverlauf, Weißraum, asymmetrisch.
//  · Eigene Mobile-Komposition (kein verkleinerter Desktop).
// Live `/abo` bleibt UNBERÜHRT bis zur Betreiber-Sichtabnahme (STOP-GATE).
import { useEffect, useRef } from "react";
import { Languages } from "lucide-react";
import AboChris from "@/components/abo/AboChris";
import "@/pages/abo/abo.css";

const DEV = (n) => `/abo/devices/${n}`;
const SHOT = (n) => `/abo/screenshots/${n}`;
const OUTFIT =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";

const LANGS = [
  ["🇩🇪", "Deutsch"],
  ["🇬🇧", "English"],
  ["🇵🇱", "Polski"],
  ["🇷🇴", "Română"],
  ["🇹🇷", "Türkçe"],
  ["🇺🇦", "Українська"],
  ["🇧🇬", "Български"],
  ["🇭🇷", "Hrvatski"],
  ["🇷🇺", "Русский"],
];

export default function AboB3DevPreview() {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = OUTFIT;
    document.head.appendChild(link);
    ref.current?.querySelectorAll(".reveal").forEach((el) => el.classList.add("in"));
    return () => link.remove();
  }, []);

  return (
    <div
      className="abo-root"
      lang="de"
      ref={ref}
      data-testid="abo-b3-dev"
      style={{ minHeight: "100vh" }}
    >
      <style>{`
        [data-testid="abo-b3-dev"] .reveal{opacity:1 !important;transform:none !important;}
        [data-testid="abo-b3-dev"] .dev-label{max-width:1280px;margin:0 auto;padding:22px 24px 0;font:600 .72rem/1.4 "Outfit",sans-serif;letter-spacing:.14em;text-transform:uppercase;color:#94a3b8;}

        /* Bühne · sehr helle CI, subtiler Sky-/Mint-Lichtverlauf (kein farbiger Kasten) */
        [data-testid="abo-b3-dev"] .b3-stage{background:radial-gradient(120% 120% at 82% -8%, #eef6fb 0%, #f2f7f4 46%, #eaf6f0 100%);padding:60px 0 78px;}
        [data-testid="abo-b3-dev"] .b3-wrap{max-width:1280px;margin:0 auto;padding:0 24px;}

        /* Kopf (links, asymmetrisch) */
        [data-testid="abo-b3-dev"] .b3-head{max-width:860px;margin:0 0 40px;}
        [data-testid="abo-b3-dev"] .b3-meta{display:flex;align-items:center;gap:12px;margin-bottom:14px;}
        [data-testid="abo-b3-dev"] .b3-kicker{font:600 .8rem/1 "Outfit",sans-serif;letter-spacing:.14em;text-transform:uppercase;color:var(--green-deep);}
        [data-testid="abo-b3-dev"] .b3-badge{font:600 .66rem/1 "Outfit",sans-serif;letter-spacing:.1em;text-transform:uppercase;color:#0f6b4a;background:#e6f6ee;border:1px solid #bfe6d2;border-radius:999px;padding:6px 11px;}
        [data-testid="abo-b3-dev"] .b3-title{font-family:var(--font-display,"Outfit",sans-serif);font-weight:700;color:var(--ink);font-size:clamp(2rem,3.4vw,3rem);line-height:1.05;margin:0 0 12px;letter-spacing:-.01em;}
        [data-testid="abo-b3-dev"] .b3-sub{font:500 1.05rem/1.5 "Outfit",sans-serif;color:var(--body,#475569);margin:0 0 14px;}
        [data-testid="abo-b3-dev"] .b3-tag{display:inline-block;font-family:var(--font-display,"Outfit",sans-serif);font-weight:700;font-size:.9rem;letter-spacing:.14em;text-transform:uppercase;color:var(--ink);background:#fff;border:1px solid #e6edf5;border-radius:999px;padding:8px 16px;box-shadow:0 10px 26px rgba(15,40,80,.08);}

        /* Komposition · Mobile: Geräte → Chris. Eigene Fassung, kein Shrink. */
        [data-testid="abo-b3-dev"] .b3-grid{display:grid;grid-template-columns:1fr;gap:34px;align-items:center;}

        /* Gerätegruppe · Mobile: iPhone groß, dann CL-Monitor groß (gestapelt) */
        [data-testid="abo-b3-dev"] .b3-devgroup{display:flex;flex-direction:column;align-items:center;gap:26px;}
        [data-testid="abo-b3-dev"] .b3-phone{margin:0;width:min(66%,260px);}
        [data-testid="abo-b3-dev"] .b3-phone img{display:block;width:100%;height:auto;filter:drop-shadow(0 26px 40px rgba(15,40,80,.24));}
        [data-testid="abo-b3-dev"] .b3-mon{margin:0;width:100%;}
        [data-testid="abo-b3-dev"] .b3-mon img{display:block;width:100%;height:auto;filter:drop-shadow(0 32px 44px rgba(15,40,80,.26));}

        /* Chris-Spalte */
        [data-testid="abo-b3-dev"] .b3-chris .abo-chris-fig{height:236px;}
        [data-testid="abo-b3-dev"] .b3-lang{margin-top:22px;}
        [data-testid="abo-b3-dev"] .b3-lang-head{display:flex;align-items:center;gap:8px;font:600 .84rem/1.3 "Outfit",sans-serif;color:var(--ink);margin:0 0 10px;}
        [data-testid="abo-b3-dev"] .b3-lang-head svg{color:var(--green-deep);}
        [data-testid="abo-b3-dev"] .b3-flags{list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:8px;}
        [data-testid="abo-b3-dev"] .b3-flags li{display:inline-flex;align-items:center;gap:6px;font:500 .82rem/1 "Outfit",sans-serif;color:var(--body,#475569);background:#fff;border:1px solid #e9eef5;border-radius:999px;padding:6px 11px;}
        [data-testid="abo-b3-dev"] .b3-flags .fl{font-size:.95rem;line-height:1;}

        @media(min-width:1000px){
          [data-testid="abo-b3-dev"] .b3-grid{grid-template-columns:60% 40%;column-gap:48px;align-items:center;}
          /* Gerätegruppe · Desktop: Monitor groß rechts-oben, iPhone überlappend links-unten */
          [data-testid="abo-b3-dev"] .b3-devgroup{display:grid;padding-bottom:24px;}
          [data-testid="abo-b3-dev"] .b3-mon{grid-area:1/1;width:90%;justify-self:end;align-self:start;}
          [data-testid="abo-b3-dev"] .b3-phone{grid-area:1/1;width:31%;justify-self:start;align-self:end;transform:translateY(9%);z-index:2;position:relative;}
          [data-testid="abo-b3-dev"] .b3-chris{align-self:center;}
          [data-testid="abo-b3-dev"] .b3-chris .abo-chris-fig{height:clamp(300px,26vw,432px);}
          [data-testid="abo-b3-dev"] .b3-chris .abo-chris-bubble{max-width:322px;font-size:1rem;}
        }
      `}</style>

      <div className="dev-label">B3 Mitarbeiterzugang · Preview · Frozen Device-/Chris-Standard · Live /abo unberührt</div>

      <section className="b3-stage" data-testid="b3-stage">
        <div className="b3-wrap">
          <header className="b3-head reveal">
            <div className="b3-meta">
              <span className="b3-kicker">Mitarbeiter-Zugang</span>
              <span className="b3-badge" data-testid="b3-badge">Grundabo</span>
            </div>
            <h2 className="b3-title" data-testid="b3-title">
              Unterweisung dort, wo es für den Mitarbeiter passt.
            </h2>
            <p className="b3-sub">Am Handy. Oder am Computer.</p>
            <span className="b3-tag" data-testid="b3-tag">Ein Inhalt. Zwei Wege.</span>
          </header>

          <div className="b3-grid">
            {/* Gerätegruppe · iPhone + CL-Monitor = zwei echte Zugangswege, gleicher Inhalt */}
            <div className="b3-devgroup reveal" data-testid="b3-devgroup">
              <figure className="b3-phone" data-testid="b3-phone">
                <img
                  src={SHOT("device-handy.webp")}
                  alt="CLartext: Mitarbeiter bearbeitet die Unterweisung am eigenen Handy – mit Lehrbildern (Gefahrenmarkierungen psychisch/mechanisch)"
                  data-testid="b3-shot-handy"
                  loading="eager"
                  draggable="false"
                />
              </figure>
              <figure className="abo-clmon b3-mon" data-testid="b3-mon">
                <img
                  src={DEV("device-b3-computer.webp")}
                  alt="CLartext auf einem hochwertigen Desktop-Gerät: derselbe Unterweisungsinhalt am Computer – mit Lehrbildern (Gefahrenmarkierungen psychisch/mechanisch)"
                  data-testid="b3-shot-computer"
                  loading="eager"
                  draggable="false"
                />
              </figure>
            </div>

            {/* Chris groß & richtungsrichtig (Pose 17 zeigt nach links zu den Geräten) + Sprachen */}
            <div className="b3-chris abo-chrisgrp reveal" data-testid="b3-chris-wrap">
              <AboChris
                pose={17}
                size="md"
                testId="b3-chris"
                text="Ich bringe die Unterweisung zu Ihren Mitarbeitern – aufs Handy oder an den Computer. Und auf Wunsch in der Muttersprache Ihrer Mitarbeiter."
              />
              <div className="b3-lang" data-testid="b3-lang">
                <p className="b3-lang-head">
                  <Languages size={17} aria-hidden="true" />
                  <span>In der Sprache, die Ihr Mitarbeiter versteht:</span>
                </p>
                <ul className="b3-flags">
                  {LANGS.map(([fl, name]) => (
                    <li key={name}>
                      <span className="fl" aria-hidden="true">{fl}</span>
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
